<template>
	<main-page :user="user" color="blue">
		<template v-slot:title>
			<template> PROVIDE TVR FEEDBACK </template>
		</template>

		<div class="sticky">
			<div class="instructions instructions-right">
				<div v-if="program && program.tvr_config && program.tvr_config.instructions" class="mb-3">
					<div
						class="btn btn-full-stripe btn-expand si-yellow text-white text-left"
						:class="{ active: instructionsExpanded }"
						@click="instructionsExpanded = !instructionsExpanded"
					>
						<h1 class="mb-0 si-font">Instructions</h1>
					</div>
					<div v-if="instructionsExpanded" class="border p-3 rich-text-html">
						<div style="text-align: left" v-html="program.tvr_config.instructions"></div>
					</div>
				</div>
				<div class="card p-4">
					<div class="d-flex flex-row justify-content-center w-100">
						<div class="mr-5">
							<div v-if="team" class="si-font si-text-yellow" style="font-size: 1.75rem; line-height: 1">
								{{ team.name }}
							</div>
							<div class="text-muted">Team</div>
						</div>
						<div>
							<div
								v-if="assignment"
								class="si-font si-text-yellow"
								style="font-size: 1.75rem; line-height: 1"
							>
								{{ assignment.name }}
							</div>
							<div class="text-muted">Assignment</div>
						</div>
					</div>
				</div>
				<table v-if="anyScores" class="table">
					<thead>
						<tr class="text-lg">
							<th class="_500" style="opacity: 0.25; width: 50%"></th>
							<th class="_500" style="opacity: 0.25; width: 25%">Score</th>
							<th class="_500" style="opacity: 0.25; width: 25%">Feedback</th>
						</tr>
					</thead>
					<tbody>
						<template v-for="cat in rubricCategories">
							<tr
								v-if="cat.response && (cat.response.score != null || cat.response.text)"
								:key="cat.id"
								style="font-size: 2rem"
							>
								<td class="v-mid si-font text-left" :class="{ [`si-text-${cat.color}`]: true }">
									{{ cat.name }}
								</td>
								<td class="v-mid si-font" :class="{ [`si-text-${cat.color}`]: true }">
									{{ cat.response.score }}
								</td>
								<td class="v-mid" :class="{ [`si-text-${cat.color}`]: true }">
									<span v-if="cat.response.text" class="material-icons" style="font-size: 30px"
										>check_circle</span
									>
								</td>
							</tr>
						</template>
					</tbody>
				</table>
			</div>
		</div>

		<div>
			<div class="content">
				<div class="feedback">
					<div class="feedback-wrapper">
						<div v-for="cat in rubricCategories" :key="cat.id" class="col-12">
							<div
								class="btn btn-full-stripe btn-expand si-orange text-white text-left"
								:class="{ active: cat.expanded, [`si-${cat.color}`]: true }"
								@click="cat.expanded = !cat.expanded"
							>
								<h1 class="mb-0 si-font">{{ cat.name }}</h1>
							</div>
							<template v-if="cat.expanded">
								<p class="text-md text-left h-pad mt-3">
									{{ cat.prompt }}
								</p>
								<h1 class="mb-0 si-font text-left h-pad" :class="{ [`si-text-${cat.color}`]: true }">
									Progress Criteria
								</h1>

								<div class="pl-2 pr-4">
									<hundred-slider
										:color="cat.color"
										:marks="fs.constructSliderMarks(cat.marks)"
										v-model="cat.response.score"
									/>
								</div>
								<h1
									class="mt-3 mb-3 si-font text-left h-pad"
									:class="{ [`si-text-${cat.color}`]: true }"
								>
									Progress Feedback
								</h1>
								<textarea class="form-control" rows="5" v-model="cat.response.text" />
							</template>
						</div>
					</div>
				</div>
			</div>
		</div>

		<template v-slot:footer>
			<div class="col-6 text-right">
				<a class="btn btn-si-secondary" href="#/tvr_picker"> Cancel </a>
				<a class="btn btn-si-secondary ml-3" @click="submit" :disabled="submitting"> Submit </a>
			</div>
		</template>

		<b-modal id="cantSubmitModal" headerClass="indigo">
			<template v-slot:modal-title>
				<h3 class="modal-title full-bar-title">Cannot Submit Pitch Feedback</h3>
			</template>
			<div class="p-2">
				<div v-for="(error, i) in submitErrors" :key="i">
					<div v-if="error.score && error.reasoning" class="text-md">
						<strong :class="{ [`si-text-${getColor(error.seq)}`]: true }">{{
							rubricCategories[error.seq].name
						}}</strong>
						requires a rating and explanation
					</div>
					<div v-if="error.score && !error.reasoning" class="text-md">
						<strong :class="{ [`si-text-${getColor(error.seq)}`]: true }">{{
							rubricCategories[error.seq].name
						}}</strong>
						requires a rating
					</div>
					<div v-if="error.reasoning && !error.score" class="text-md">
						<strong :class="{ [`si-text-${getColor(error.seq)}`]: true }">{{
							rubricCategories[error.seq].name
						}}</strong>
						requires an explanation
					</div>
				</div>
			</div>
			<template v-slot:modal-footer="{ cancel }">
				<button type="button" class="btn btn-primary" @click="cancel">Okay</button>
			</template>
		</b-modal>
	</main-page>
</template>

<script>
import MainPage from "@/components/MainPage";
import HundredSlider from "@/components/HundredSlider";

import ProgramService from "@/services/ProgramService";
import SectionService from "@/services/SectionService";
import TVRService from "@/services/TVRService";
import RubricService from "@/services/RubricService";
import Notie from "@/services/NotieService";
import BB from "bluebird";
import fs from "@/services/FormatService";
import _ from "lodash";

export default {
	name: "TVRFeedback",

	props: ["user"],

	components: { MainPage, HundredSlider },

	data() {
		let teamID = this.$route.params.teamID;
		let assignmentID = this.$route.params.assignmentID;
		let recipientID = this.$route.params.recipientID;

		return {
			fs: fs,
			teamID: teamID,
			assignmentID: assignmentID,
			recipientID: recipientID,
			instructionsExpanded: true,

			context: null,
			program: null,
			team: null,
			assignment: null,
			rubric: null,
			rubricCategories: null,
			submitErrors: [],
			submitting: false,
		};
	},

	created() {
		this.loadData();
	},

	computed: {
		anyScores() {
			if (!this.rubric) {
				return false;
			}
			return RubricService.anyScore(this.rubric);
		},
	},

	methods: {
		loadData() {
			BB.props({
				program: ProgramService.getContextProgram(),
				context: SectionService.getContextPrefilled(),
				feedbacks: TVRService.getMyFeedbacks(),
			})
				.then((r) => {
					this.program = r.program.data;
					this.context = r.context.data;
					this.team = _.find(this.context.teams, { id: this.teamID });
					this.assignment = _.find(this.context.tvrs, { id: this.assignmentID });

					this.rubric = this.program && this.program.tvr_config && this.program.tvr_config.rubric;
					RubricService.prepForScore(this, this.rubric);
					this.rubricCategories = this.rubric ? this.rubric.categories : [];
					_.each(this.rubricCategories, (cat, i) => {
						this.$set(cat, "expanded", false);
						cat.color = this.getColor(i);
					});

					let thisFeedback = _.find(r.feedbacks.data, {"recipient_id": this.recipientID, "assignment_id": this.assignmentID });
					if (thisFeedback) {
						_.each(thisFeedback.response.responses, (resp) => {
							let cat = _.find(this.rubric.categories, {"id": resp.category_id})
							if (cat) {
								cat.response = resp;
							}
						})
					}

				})
				.catch((e) => {
					console.log(e);
					Notie.error("Failed to load Team Value Rubric", e);
				});
		},

		constructFeedback() {
			let feedback = {
				team_id: this.teamID,
				assignment_id: this.assignmentID,
				recipient_id: this.recipientID,
				response: {
					rubric_id: this.rubric.id,
					responses: [],
				},
			};

			_.each(this.rubric.categories, (cat) => {
				feedback.response.responses.push(cat.response);
			});

			return feedback;
		},

		submit() {
			if (this.submitting) {
				return;
			}
			this.submitting = true;
			this.validateSubmit();
			if (this.submitErrors && this.submitErrors.length > 0) {
				this.$bvModal.show("cantSubmitModal");
				this.submitting = false;
				return;
			}

			let feedback = this.constructFeedback();
			TVRService.submitFeedback(feedback)
				.then(() => {
					Notie.success("Feedback submitted");
					this.$router.push("/tvr_picker");
				})
				.catch((e) => {
					console.log(e);
					Notie.error("Failed to submit feedback", e);
					this.submitting = false;
				});
		},

		getColor(index) {
			if (index % 2 == 1) {
				return "green";
			}
			if (index % 4 == 0) {
				return "orange";
			}
			if (index % 4 == 2) {
				return "blue";
			}
		},

		validateSubmit() {
			this.submitErrors = [];

			_.each(this.rubricCategories, (rc, i) => {
				let any = false;
				let error = {
					seq: i,
				};
				if (rc.required_score && (rc.response.score == null || rc.response.score == undefined)) {
					any = true;
					error.score = true;
				}
				if (rc.required_reasoning && !rc.response.text) {
					any = true;
					error.reasoning = true;
				}
				if (any) {
					this.submitErrors.push(error);
				}
			});
		},
	},
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.content {
	margin-bottom: 3rem;
}
.content .col-12 {
	margin-top: 3rem;
}

.h-pad {
	padding: 0rem 2rem;
}

.instructions {
	display: block;
	top: 0;
	margin-left: calc(50% - 640px);
	width: 1280px;
	transition: margin-left 0.5s ease-in-out, width 0.5s ease-in-out;
}
.instructions.instructions-right {
	margin-left: calc(50% + 80px);
	width: 560px;
}

.feedback {
	display: block;
	width: 700px;
	margin-left: calc(50% - 640px);
	margin-right: 580px;
	transition: opacity 0.5s;
}


.rich-text-html {
	height: 400px;
	overflow: scroll;
}

@media screen and (max-width: 1360px) {
  .instructions.instructions-right {
    margin-left: 80px;
    width: 700px;
  }
  .feedback {
    margin-left: 80px;
  }
}

@media screen and (max-width: 780px) {
  .instructions {
    margin-left: 0px;
    width: 300px;
  }
  .instructions.instructions-right {
    margin-left: 0px;
    width: 100%;
  }
  .feedback {
    margin-left: 0px;
    width: 100%;
  }
}

@media screen and (min-width: 1360px) {
  .sticky {
    position: fixed;
    margin-top: 3rem;
    margin-bottom: 3rem;
    height: calc(100% - 14rem);
    width: 100%;
  }
  .instructions {
    max-height: 100%;
    overflow-y: scroll;
  }
}

textarea {
	border-width: 2px !important;
	border-color: rgb(200, 200, 200) !important;
}

.v-mid {
	vertical-align: middle;
}

td {
	padding-top: 0.25rem;
	padding-bottom: 0.25rem;
}
</style>
